import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  Pagination,
  Skeleton,
  Chip,
} from '@mui/material'
import React, { useState } from 'react'
import { formatDate, SeverityMap } from '../_helpers'
import { useNavigate } from 'react-router-dom'
import { useDashboardTableMeasures } from '../../../../queries/hooks/useDashboardTableMeasures'
import { getErrorMessage } from '../../../../util/errorHandling'
import useDashboardListFilters from '../../../../util/useDashboardListFilters'
import { useResetDashboardPage } from '../_hooks'
import {
  ListPaginationSkeleton,
  DASHBOARD_ROW_MIN_HEIGHT,
  DASHBOARD_ROW_PADDING_Y,
  DASHBOARD_CHIP_SLOT_HEIGHT,
} from '../../dashboardListUi'

export const AlertsList = () => {
  const navigate = useNavigate()
  const { filters } = useDashboardListFilters()
  const [currentPage, setCurrentPage] = useState(1)
  const alertsPerPage = 10
  const { data, isLoading, error } = useDashboardTableMeasures({
    page: currentPage,
    limit: alertsPerPage,
  })
  const alerts = data?.results ?? []
  const pagination = data?.pagination

  useResetDashboardPage({ filters, setCurrentPage })

  const handleAlertClick = (auditId: string) => {
    navigate(`/alerts?auditId=${auditId}`)
  }

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page)
  }

  const getSeverityChipColor = (severity: number) => {
    if (severity === 3) return 'error'
    if (severity === 2) return 'warning'
    return 'info'
  }

  const totalPages = pagination?.pages ?? 0
  const totalAlerts = pagination?.total ?? alerts.length

  const renderLoadingContent = () => {
    const placeholderRows = Array.from(
      { length: alertsPerPage },
      (_, rowNumber) => `alert-${rowNumber + 1}`
    )

    return (
      <>
        <List>
          {placeholderRows.map((rowId) => (
            <React.Fragment key={`alert-loading-${rowId}`}>
              <ListItem
                alignItems="flex-start"
                sx={{ minHeight: DASHBOARD_ROW_MIN_HEIGHT, py: DASHBOARD_ROW_PADDING_Y }}
              >
                <ListItemText
                  sx={{ my: 0 }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Skeleton variant="rounded" width="65%" height={DASHBOARD_CHIP_SLOT_HEIGHT} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Skeleton variant="text" width="55%" height={18} />
                    </Box>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
        <ListPaginationSkeleton qaId="alerts-list-pagination-loading" />
      </>
    )
  }

  const renderErrorContent = () => (
    <Typography variant="body2" color="text.secondary">
      {getErrorMessage(error, 'Failed to load alerts')}
    </Typography>
  )

  const renderEmptyContent = () => (
    <Typography variant="body2" color="text.secondary">
      No alerts found in the selected date range.
    </Typography>
  )

  const renderAlertsContent = () => (
    <>
      <List>
        {alerts.map((alert, index) => {
          const type = SeverityMap[alert.severity as keyof typeof SeverityMap]
          return (
            <React.Fragment key={index}>
              <ListItem
                alignItems="flex-start"
                button
                onClick={() => handleAlertClick(alert.documentId)}
                sx={{
                  cursor: 'pointer',
                  minHeight: DASHBOARD_ROW_MIN_HEIGHT,
                  py: DASHBOARD_ROW_PADDING_Y,
                }}
              >
                <ListItemText
                  sx={{ my: 0 }}
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: DASHBOARD_CHIP_SLOT_HEIGHT,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          maxWidth: '50%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        data-qa-id={`alerts-list-measure-title-${alert.measureTitle || index}`}
                      >
                        {alert.measureTitle}
                      </Typography>
                      <Chip
                        label={type.charAt(0).toUpperCase() + type.slice(1)}
                        size="small"
                        color={getSeverityChipColor(alert.severity)}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Document: <strong>{alert.documentTitle}</strong> •{' '}
                      {formatDate(alert.documentAt)}
                    </Typography>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          )
        })}
      </List>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            data-qa-id="alerts-list-pagination"
          />
        </Box>
      )}
    </>
  )

  const renderContent = () => {
    if (isLoading) return renderLoadingContent()
    if (error) return renderErrorContent()
    if (alerts.length === 0) return renderEmptyContent()
    return renderAlertsContent()
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Alerts{' '}
        {isLoading ? (
          <Skeleton variant="text" width={28} sx={{ display: 'inline-block' }} />
        ) : (
          `(${totalAlerts})`
        )}
      </Typography>
      {renderContent()}
    </Box>
  )
}
